<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import SettingGuide from '../../SettingGuide.svelte';
	import { Container, Text, TokenV2 } from '@Shelly/shelly-design-system';
	import { devices_config } from '@devices/index';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { get_mode } from 'src/helpers/interface_helpers';
	import { getEmeterSetConfigMethod } from 'src/helpers/settings/emeter_settings_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { genCharArray } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';

	export let device: Device;

	const settings = readDeviceSettings(device.id);
	const emSettings = settings?.['em:0'];
	const blinkModes = ['active_energy', 'apparent_energy'] as const;
	const method = getEmeterSetConfigMethod(device);
	const mode = get_mode(device, readDeviceStatus(device.id), settings);
	const phasesNumber = devices_config[device.type]?.modes?.[mode]?.phases;
	const phases = [...genCharArray(phasesNumber), 'all'];

	let blinkMode: string = emSettings?.blink_mode_selector || 'active_energy';
	let phaseSelector: string = emSettings?.phase_selector || 'all';
	let savedBlinkMode = blinkMode;
	let savedPhaseSelector = phaseSelector;
	let loading = false;

	$: blinkDirty = blinkMode !== savedBlinkMode;
	$: phaseDirty = phaseSelector !== savedPhaseSelector;
	$: isDirty = blinkDirty || phaseDirty;
	$: saveDisabled = loading || !isDirty;

	const blinkLabel = (modeKey: string): string => {
		if (modeKey === 'apparent_energy') {
			return $format('label_apparent_energy', { default: 'Apparent energy' });
		}
		return $format('label_active_energy', { default: 'Active energy' });
	};

	const phaseLabel = (phase: string): string => {
		if (phase === 'all') {
			return $format('option_command_all', { default: 'All' });
		}
		return $format(`label_phase_${phase}`, { default: `Phase ${phase}` });
	};

	const save = async () => {
		if (saveDisabled) return;

		const config: Record<string, string> = {};
		if (blinkDirty) config.blink_mode_selector = blinkMode;
		if (phaseDirty) config.phase_selector = phaseSelector;

		loading = true;
		const res: SettingRequest = await gen2Request(device, method, {
			id: 0,
			config,
		});
		if (res?.isok) {
			if (blinkDirty) savedBlinkMode = blinkMode;
			if (phaseDirty) savedPhaseSelector = phaseSelector;
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}
</script>

<div class="emeter-led-indicator">
	<SettingGuide
		text={$format('settings_guide_emeter_led_indicator', {
			default: 'Choose what the Count LED reports and which phase drives it.',
		})}
	/>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{$format('settings_heading_emeter_blink_mode', {
				default: 'Blink mode',
			})}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('blink_mode_selector_description', {
				default: 'Select the electrical measure that controls the LED indicator.',
			})}
		</Text>
		<div class="d-flex flex-wrap">
			{#each blinkModes as modeKey (modeKey)}
				<TokenV2
					id={`token-emeter-blink-${modeKey}`}
					selected={blinkMode === modeKey}
					size="m"
					title={blinkLabel(modeKey)}
					class="mr-s mb-s"
					aria-label={blinkLabel(modeKey)}
					on:click={() => {
						blinkMode = modeKey;
					}}
				/>
			{/each}
		</div>
	</Container>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{$format('settings_heading_emeter_led_phase', {
				default: 'LED phase',
			})}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('led_phase_selector_description', {
				default: 'Select which phase controls the LED indicator.',
			})}
		</Text>
		<div class="d-flex flex-wrap">
			{#each phases as phase (phase)}
				<TokenV2
					id={`token-emeter-led-phase-${phase}`}
					selected={phaseSelector === phase}
					size="m"
					title={phaseLabel(phase)}
					class="mr-s mb-s"
					aria-label={phaseLabel(phase)}
					on:click={() => {
						phaseSelector = phase;
					}}
				/>
			{/each}
		</div>
	</Container>
</div>

<style>
	.emeter-led-indicator {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
